import { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import ClientGrowthDrawdownChart from "components/client/ClientGrowthDrawdownChart";
import { MdOutlineSpaceDashboard } from "react-icons/md";
import DashboardAccountRiskControls from "components/client/DashboardAccountRiskControls";
import { useClientDashboardData } from "hooks/useClientDashboardData";
import { apiUrl } from "utils/apiUrl";
import { readClientSession } from "utils/clientSession";

function parseBalanceDisplay(balanceDisplay) {
  if (!balanceDisplay) return null;
  const s = String(balanceDisplay).trim();
  const m = s.match(/^([\d,.]+)\s*(.*)$/);
  if (!m) return null;
  const amount = Number(m[1].replace(/,/g, ""));
  if (!Number.isFinite(amount)) return null;
  return { amount, currency: (m[2] || "").trim() };
}

function StatCard({ title, value, hint }) {
  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm dark:border-white/10 dark:bg-navy-800">
      <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
        {title}
      </p>
      <p className="mt-2 text-2xl font-bold tabular-nums text-navy-700 dark:text-white">
        {value}
      </p>
      {hint ? (
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-500">{hint}</p>
      ) : null}
    </div>
  );
}

export default function ClientDashboard() {
  const location = useLocation();
  const session = readClientSession();
  const devGuest = process.env.NODE_ENV === "development" && !session;
  const {
    accounts,
    perfAccounts,
    loading,
    perfLoading,
    error,
    refresh,
    effectiveClientId,
  } = useClientDashboardData(session);
  const [paidSlotsCount, setPaidSlotsCount] = useState(0);
  const [awaitingApprovalSlots, setAwaitingApprovalSlots] = useState([]);

  const first = (session?.firstName || "").trim();
  const greetingName = devGuest
    ? "Guest"
    : first || (session?.email ? session.email.split("@")[0] : "") || "Member";

  useEffect(() => {
    let cancelled = false;
    const loadSlots = async () => {
      try {
        const res = await fetch(apiUrl("/api/client/slots/owned"), {
          credentials: "include",
          cache: "no-store",
        });
        if (!res.ok) return;
        const data = await res.json().catch(() => ({}));
        const n = Number(data?.paidSlotsCount);
        if (!cancelled && Number.isFinite(n) && n >= 0) {
          setPaidSlotsCount(n);
        }
        if (!cancelled && Array.isArray(data?.awaitingApprovalSlots)) {
          setAwaitingApprovalSlots(data.awaitingApprovalSlots);
        }
      } catch {
        /* ignore */
      }
    };
    void loadSlots();
    const shouldPoll =
      location.state?.paymentPendingApproval === true ||
      awaitingApprovalSlots.length > 0;
    const intervalId = shouldPoll
      ? window.setInterval(() => void loadSlots(), 12000)
      : null;
    return () => {
      cancelled = true;
      if (intervalId) window.clearInterval(intervalId);
    };
  }, [
    effectiveClientId,
    location.state?.paymentPendingApproval,
    awaitingApprovalSlots.length,
  ]);

  const showPendingApprovalBanner =
    location.state?.paymentPendingApproval === true ||
    awaitingApprovalSlots.length > 0;

  const pendingApprovalSlotNames = useMemo(() => {
    const fromState = String(location.state?.slotName || "").trim();
    const fromApi = awaitingApprovalSlots
      .map((s) => String(s.orderId || s.codification || "").trim())
      .filter(Boolean);
    if (fromState && !fromApi.includes(fromState)) {
      return [fromState, ...fromApi];
    }
    return fromApi.length > 0 ? fromApi : fromState ? [fromState] : [];
  }, [awaitingApprovalSlots, location.state?.slotName, location.state?.paymentPendingApproval]);

  const connectedAccounts = useMemo(
    () => accounts.filter((a) => a.brokerConnected),
    [accounts],
  );

  const balanceSummary = useMemo(() => {
    let total = 0;
    let currency = "";
    for (const acc of accounts) {
      const parsed = parseBalanceDisplay(acc.balanceDisplay);
      if (!parsed || !acc.brokerConnected) continue;
      total += parsed.amount;
      if (!currency && parsed.currency) currency = parsed.currency;
    }
    return { total, currency };
  }, [accounts]);

  const totalBalanceLabel = useMemo(() => {
    if (connectedAccounts.length === 0) return "—";
    if (balanceSummary.total <= 0) {
      return "—";
    }
    const cur = balanceSummary.currency ? ` ${balanceSummary.currency}` : "";
    return `${balanceSummary.total.toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}${cur}`;
  }, [connectedAccounts.length, balanceSummary]);

  return (
    <div className="space-y-6">
      {showPendingApprovalBanner ? (
        <div
          className="rounded-2xl border border-amber-300/70 bg-amber-50 px-5 py-4 text-sm text-amber-950 shadow-sm dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-100"
          role="status"
        >
          <p className="font-semibold">Waiting for Admin Approval</p>
          <p className="mt-1 text-amber-900 dark:text-amber-200/90">
            Your payment was received. An administrator will verify it
            and approve your slot
            {pendingApprovalSlotNames.length > 0
              ? ` (${pendingApprovalSlotNames.join(", ")})`
              : ""}
            . You can connect your trading account once approval is complete.
          </p>
        </div>
      ) : null}
      <section className="rounded-2xl border border-gray-200 bg-gradient-to-br from-white to-gray-50 p-6 shadow-sm dark:border-white/10 dark:from-navy-800 dark:to-navy-900 md:p-8">
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div className="flex items-start gap-4">
            <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-brand-500/10 text-brand-600 dark:bg-brand-400/15 dark:text-brand-300">
              <MdOutlineSpaceDashboard className="h-7 w-7" aria-hidden />
            </span>
            <div>
              <h2 className="text-xl font-bold text-navy-700 dark:text-white md:text-2xl">
                Welcome back, {greetingName}
              </h2>
              <p className="mt-1 max-w-xl text-sm leading-relaxed text-gray-600 dark:text-gray-400">
                Overview of your connected trading accounts and balances.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => void refresh()}
            disabled={loading || perfLoading}
            className="shrink-0 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-navy-700 shadow-sm hover:bg-gray-50 disabled:opacity-50 dark:border-white/15 dark:bg-white/5 dark:text-white dark:hover:bg-white/10"
          >
            {loading || perfLoading ? "Refreshing…" : "Refresh"}
          </button>
        </div>
      </section>

      {error ? (
        <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
      ) : null}

      {!effectiveClientId ? (
        <p className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-100">
          Sign in to load your accounts and performance chart.{" "}
          <Link
            to="/SignIn"
            className="font-semibold text-brand-600 underline hover:no-underline dark:text-brand-300"
          >
            Go to login
          </Link>
        </p>
      ) : null}

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
        <StatCard
          title="Connected accounts"
          value={
            loading ? "…" : `${connectedAccounts.length} / ${paidSlotsCount}`
          }
          hint="Connected / paid slots"
        />
        <StatCard
          title="Total balance (connected)"
          value={loading ? "…" : totalBalanceLabel}
          hint="Sum of connected account balances"
        />
        <StatCard
          title="Paid slots"
          value={String(paidSlotsCount)}
          hint="Confirmed subscriptions"
        />
      </div>

      <section className="rounded-[20px] border border-gray-200 bg-white p-6 shadow-sm dark:border-white/10 dark:bg-navy-800">
        <h3 className="text-lg font-bold text-navy-700 dark:text-white">
          Growth & drawdown
        </h3>
        <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
          Growth % uses your account <strong>balance</strong> (closed P&amp;L), not
          floating equity from open copied positions. Small moves under ~1.5% stay
          flat at 0% until a real balance change from closed trades.
        </p>
        <div className="mt-4 rounded-xl border border-gray-100 p-2 dark:border-white/10">
          <ClientGrowthDrawdownChart
            accounts={perfAccounts}
            loading={loading || perfLoading}
          />
        </div>
      </section>

      <section className="rounded-[20px] border border-gray-200 bg-white p-6 shadow-sm dark:border-white/10 dark:bg-navy-800">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h3 className="text-lg font-bold text-navy-700 dark:text-white">
              Trading accounts
            </h3>
            <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
              Accounts linked to your subscription slots.
            </p>
          </div>
          <Link
            to="/client/subscription"
            className="text-sm font-semibold text-brand-500 hover:underline dark:text-brand-300"
          >
            Manage in Subscription →
          </Link>
        </div>

        {loading && accounts.length === 0 ? (
          <p className="mt-6 text-sm text-gray-500">Loading accounts…</p>
        ) : accounts.length === 0 ? (
          <p className="mt-6 text-sm text-gray-500 dark:text-gray-400">
            No trading accounts yet. Add one under{" "}
            <Link
              to="/client/subscription"
              className="font-semibold text-brand-500 hover:underline dark:text-brand-300"
            >
              Subscription
            </Link>
            .
          </p>
        ) : effectiveClientId ? (
          <DashboardAccountRiskControls
            accounts={accounts}
            perfAccounts={perfAccounts}
            effectiveClientId={effectiveClientId}
          />
        ) : (
          <ul className="mt-4 space-y-2">
            {accounts.map((row) => (
              <li
                key={row.id}
                className="rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm dark:border-white/10 dark:bg-navy-900/50"
              >
                Sign in to manage risk settings.
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
